import { compact } from "lodash";
import { useMemo, useState } from "react";
import {
  AssessmentGroupAnonymity,
  AssessmentGroupDelivery,
  AssessmentGroupListFragmentFragment,
  AssessmentQuestionComment,
  AssessmentQuestionResponseVisibility,
  AssessmentQuestionResponses,
  AssessmentTemplateComplianceRequirement,
  AssessmentType,
  Maybe,
  OrganizationTalentIndicatorFragment,
  SaveComplianceProgramMutationVariables,
} from "types/graphql-schema";

import useLabel from "@apps/use-label/use-label";
import Button from "@components/button/button";
import { buttonTheme } from "@components/button/button-constants";
import { ComboboxGenericOption } from "@components/combobox/generic-combobox";
import FormFields from "@components/form/form-fields";
import DeprecatedSelect from "@components/select/select";
import ToggleSwitch from "@components/toggle-switch/toggle-switch";
import InfoTooltip from "@components/tooltip/info-tooltip";
import WarningTooltip from "@components/tooltip/warning-tooltip";
import { TopicflowIconMap } from "@components/topicflow-icon/topicflow-icon-helpers";
import {
  assessmentGroupAnonymityOptions,
  assessmentGroupDeliveryOptions,
} from "@helpers/constants";
import { assertNonNull } from "@helpers/helpers";

import AssessmentGroupSidebar from "../assessment-group-sidebar/assessment-group-sidebar";
import TalentIndicatorSidebar from "../talent-indicator-sidebar/talent-indicator-sidebar";
import {
  AssessmentTemplateExtraSettings,
  ComplianceProgramStep,
} from "../types";
import ComplianceProgramFormStep from "./compliance-program-form-step";
import QuestionSetSelector from "./question-set-selector";

const UpwardAssessmentStep = ({
  proposedComplianceProgram,
  canUpdate,
  hideSettings,
  complianceProgramSteps,
  talentIndicatorData,
  onUpdateComplianceProgramSteps,
  onUpdateAssessmentTemplateSettings,
  onUpdateAssessmentTemplate,
  availableAssessmentGroups,
  onRemoveAssessmentTemplate,
  onAssessmentGroupCreated,
  onAssessmentGroupArchived,
}: {
  proposedComplianceProgram: SaveComplianceProgramMutationVariables;
  canUpdate: boolean;
  hideSettings?: boolean;
  complianceProgramSteps: ComplianceProgramStep[];
  talentIndicatorData?: OrganizationTalentIndicatorFragment[];
  onUpdateComplianceProgramSteps: (steps: ComplianceProgramStep[]) => void;
  onUpdateAssessmentTemplateSettings: (
    assessmentType: AssessmentType,
  ) => (settings: AssessmentTemplateExtraSettings) => void;
  onUpdateAssessmentTemplate: (
    assessmentType: AssessmentType,
  ) => (opt: ComboboxGenericOption<number> | null) => void;
  availableAssessmentGroups: AssessmentGroupListFragmentFragment[];
  onRemoveAssessmentTemplate: (assessmentType: AssessmentType) => void;
  onAssessmentGroupCreated: (
    newAssessmentGroup: AssessmentGroupListFragmentFragment,
  ) => void;
  onAssessmentGroupArchived: (
    assessmentType: AssessmentType,
    assessmentGroupId: number,
  ) => void;
}) => {
  const label = useLabel();

  const [isAssessmentGroupSidebarOpen, setIsAssessmentGroupSidebarOpen] =
    useState(false);
  const [isTalentIndicatorSidebarOpen, setIsTalentIndicatorSidebarOpen] =
    useState(false);
  const [assessmentGroupToDuplicate, setAssessmentGroupToDuplicate] =
    useState<AssessmentGroupListFragmentFragment | null>(null);

  const availableAssessmentGroupSelectOptions = useMemo(() => {
    return availableAssessmentGroups
      .filter(({ assessmentType }) => assessmentType === AssessmentType.Manager)
      .filter((assessmentGroup) => !assessmentGroup.hasNpsQuestions)
      .map(({ id, title }) => ({
        value: id,
        label: title,
      }));
  }, [availableAssessmentGroups]);

  const selectedAssessmentTemplates = useMemo(
    () =>
      proposedComplianceProgram.assessmentTemplates
        ? compact(
            proposedComplianceProgram.assessmentTemplates as Maybe<AssessmentTemplateComplianceRequirement>[],
          )
        : [],
    [proposedComplianceProgram.assessmentTemplates],
  );

  const selectedManagerAssessmentTemplate = useMemo(() => {
    return selectedAssessmentTemplates.find(
      (template) => template.assessmentType === AssessmentType.Manager,
    );
  }, [selectedAssessmentTemplates]);

  const selectedAssessmentGroup = useMemo(() => {
    return selectedManagerAssessmentTemplate &&
      selectedManagerAssessmentTemplate.questionSetIds.length > 0
      ? (availableAssessmentGroups.find(
          ({ id }) =>
            id === selectedManagerAssessmentTemplate.questionSetIds[0],
        ) ?? null)
      : null;
  }, [availableAssessmentGroups, selectedManagerAssessmentTemplate]);

  const managerDeliveryOptions = useMemo(
    () => assessmentGroupDeliveryOptions(label, AssessmentType.Manager),
    [label],
  );

  const applicableTalentIndicators = useMemo(() => {
    return talentIndicatorData
      ? talentIndicatorData.filter(
          (talentIndicator) => talentIndicator.isManagerIndicator,
        )
      : [];
  }, [talentIndicatorData]);

  return (
    <>
      {isTalentIndicatorSidebarOpen && (
        <TalentIndicatorSidebar
          onClose={() => setIsTalentIndicatorSidebarOpen(false)}
          canUpdate={canUpdate}
          assessmentType={AssessmentType.Manager}
          talentIndicatorData={applicableTalentIndicators}
          talentIndicatorSetup={
            selectedManagerAssessmentTemplate?.talentIndicatorConfig ?? {
              talentIndicators: [],
            }
          }
          onUpdateTalentIndicators={(talentIndicatorConfig) => {
            onUpdateAssessmentTemplateSettings(AssessmentType.Manager)({
              talentIndicatorConfig,
            });
          }}
        />
      )}
      {isAssessmentGroupSidebarOpen && (
        <AssessmentGroupSidebar
          assessmentType={AssessmentType.Manager}
          assessmentGroupId={selectedAssessmentGroup?.id}
          assessmentGroupToDuplicateId={assessmentGroupToDuplicate?.id}
          onClose={() => {
            setAssessmentGroupToDuplicate(null);
            setIsAssessmentGroupSidebarOpen(false);
          }}
          onAssessmentGroupCreated={(assessmentGroup) => {
            onAssessmentGroupCreated(assessmentGroup);
            onUpdateAssessmentTemplate(AssessmentType.Manager)({
              value: assessmentGroup.id,
              label: assessmentGroup.title,
            });
          }}
          onAssessmentGroupArchived={(assessmentGroupId) =>
            onAssessmentGroupArchived(AssessmentType.Manager, assessmentGroupId)
          }
        />
      )}
      <ComplianceProgramFormStep
        canDelete={canUpdate && !hideSettings}
        title={`Upward ${label("review", { capitalize: true })}`}
        onDelete={() => {
          onRemoveAssessmentTemplate(AssessmentType.Manager);
          onUpdateComplianceProgramSteps(
            complianceProgramSteps.filter(
              (s) => s !== ComplianceProgramStep.UPWARD_REVIEW,
            ),
          );
        }}
      >
        <div className="p-6 flex flex-col gap-3">
          <div className="text-sm text-gray-600 font-medium mb-4">
            Sections (For Form Input)
          </div>
          <div className="flex items-center gap-3 p-4 bg-white border rounded">
            <QuestionSetSelector
              assessmentType={AssessmentType.Manager}
              availableAssessmentGroupSelectOptions={
                availableAssessmentGroupSelectOptions
              }
              canUpdate={canUpdate}
              onUpdateAssessmentTemplate={onUpdateAssessmentTemplate}
              onDuplicateAssessmentGroup={setAssessmentGroupToDuplicate}
              onOpenAssessmentGroupSidebar={() =>
                setIsAssessmentGroupSidebarOpen(true)
              }
              selectedAssessmentGroup={selectedAssessmentGroup}
            />
          </div>

          {waffle.flag_is_active("talent-indicators") && (
            <div className="flex items-center justify-between p-4 bg-white border rounded">
              <div className="flex items-center gap-2">
                <div className="text-sm">Manager Talent Indicators</div>
                <InfoTooltip text="Use this if you would like to assess managers on standard talent indicators." />
              </div>

              {!applicableTalentIndicators ? (
                <WarningTooltip
                  size={6}
                  text="There was a problem loading talent indicators"
                />
              ) : (
                <div className="flex items-center gap-4">
                  <Button
                    className="mb-1"
                    disabled={
                      !selectedManagerAssessmentTemplate ||
                      !selectedManagerAssessmentTemplate?.talentIndicatorConfig ||
                      selectedManagerAssessmentTemplate?.talentIndicatorConfig
                        .talentIndicators?.length === 0
                    }
                    icon={TopicflowIconMap.edit}
                    theme={buttonTheme.iconGray}
                    onClick={() => setIsTalentIndicatorSidebarOpen(true)}
                  />
                  <ToggleSwitch
                    disabled={!canUpdate || !selectedManagerAssessmentTemplate}
                    checked={
                      !!selectedManagerAssessmentTemplate?.talentIndicatorConfig &&
                      selectedManagerAssessmentTemplate.talentIndicatorConfig
                        .talentIndicators.length > 0
                    }
                    onChange={(enabled) => {
                      onUpdateAssessmentTemplateSettings(
                        AssessmentType.Manager,
                      )({
                        talentIndicatorConfig: enabled
                          ? {
                              talentIndicators: applicableTalentIndicators.map(
                                (talentIndicator) => ({
                                  talentIndicatorId: talentIndicator.id,
                                  responses:
                                    AssessmentQuestionResponses.ExcludeSelfAssessment,
                                  responseVisibility:
                                    AssessmentQuestionResponseVisibility.HiddenFromSubject,
                                  comment: AssessmentQuestionComment.Optional,
                                  description: talentIndicator.description,
                                }),
                              ),
                            }
                          : { talentIndicators: [] },
                      });
                    }}
                  />
                </div>
              )}
            </div>
          )}
        </div>

        {!hideSettings && (
          <div className="p-6 border-t">
            <div className="text-sm text-gray-600 font-medium mb-4">
              Settings
            </div>
            <FormFields
              fields={[
                {
                  title: "Anonymity",
                  render: () =>
                    selectedManagerAssessmentTemplate ? (
                      <DeprecatedSelect<AssessmentGroupAnonymity>
                        disabled={!canUpdate}
                        value={assertNonNull(
                          selectedManagerAssessmentTemplate.anonymity,
                        )}
                        options={assessmentGroupAnonymityOptions(
                          AssessmentType.Manager,
                        )}
                        onChange={(opt) =>
                          onUpdateAssessmentTemplateSettings(
                            AssessmentType.Manager,
                          )({
                            anonymity: opt.value,
                          })
                        }
                      />
                    ) : (
                      <div className="text-sm h-10 flex items-center">-</div>
                    ),
                },
                {
                  title: "Delivery",
                  render: () =>
                    selectedManagerAssessmentTemplate ? (
                      <DeprecatedSelect<AssessmentGroupDelivery>
                        disabled={!canUpdate}
                        value={assertNonNull(
                          selectedManagerAssessmentTemplate.delivery,
                        )}
                        options={managerDeliveryOptions}
                        onChange={(opt) =>
                          onUpdateAssessmentTemplateSettings(
                            AssessmentType.Manager,
                          )({
                            delivery: opt.value,
                          })
                        }
                      />
                    ) : (
                      <div className="text-sm h-10 flex items-center">-</div>
                    ),
                },
              ]}
            />
          </div>
        )}
      </ComplianceProgramFormStep>
    </>
  );
};
export default UpwardAssessmentStep;
